export type FolderVideoQuality = '240' | '360';
export class FolderVideoAccessError extends Error {}

export function initialFolderVideoQuality(): FolderVideoQuality {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string; downlink?: number } }).connection;
  return connection?.saveData || /(^|-)2g|3g/.test(connection?.effectiveType || '') || (connection?.downlink !== undefined && connection.downlink < 1) ? '240' : '360';
}

/** Keep the relative path in the route so every relative HLS segment retains its authorization scope. */
export function folderStreamEndpoint(original: string, quality: FolderVideoQuality): string {
  const url = new URL(original, window.location.origin);
  const match = url.pathname.match(/^(\/api\/explorer\/public\/[A-Za-z0-9_-]{22}\/folder)\/view$/);
  const path = url.searchParams.get('path');
  if (url.origin !== window.location.origin || !match || !path) throw new FolderVideoAccessError('Недопустимый источник видео.');
  const encoded = btoa(Array.from(new TextEncoder().encode(path), byte => String.fromCharCode(byte)).join('')).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  return `${match[1]}/hls/${quality}/${encoded}`;
}

export const folderVideoRetryDelay = (attempt: number) => Math.min(15000, 1000 * 2 ** Math.min(attempt - 1, 4));

export async function prepareFolderVideo(original: string, quality: FolderVideoQuality, signal: AbortSignal, status: (value: string) => void, retry = false): Promise<string> {
  const endpoint = folderStreamEndpoint(original, quality);
  const deadline = Date.now() + 15 * 60 * 1000;
  let first = true, failures = 0;
  while (Date.now() < deadline) {
    signal.throwIfAborted();
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, 15000);
    try {
      const response = await fetch(`${endpoint}/prepare${retry && first ? '?retry=1' : ''}`, { credentials: 'same-origin', signal: controller.signal });
      if ([401, 403, 404, 410].includes(response.status)) throw new FolderVideoAccessError('Доступ к видео закончился. Откройте ссылку снова.');
      if ([408, 429].includes(response.status) || response.status >= 500) throw new Error('Temporary network failure');
      if (!response.ok) throw new FolderVideoAccessError('Этот файл не удалось подготовить для просмотра.');
      const { data } = await response.json() as { data?: { status: string; generation?: string } };
      if (data?.status === 'ready' && /^[a-f0-9]{64}-[a-f0-9]{16}$/.test(data.generation || '')) return `${endpoint}/${data.generation}/index.m3u8`;
      if (!data || !['queued', 'processing', 'busy'].includes(data.status)) throw new FolderVideoAccessError('Не удалось подготовить видео. Повторите попытку.');
      status(data.status === 'processing' ? 'Готовим первые секунды видео…' : 'Видео ожидает подготовки…');
      failures = 0; first = false;
    } catch (error) {
      signal.throwIfAborted();
      if (error instanceof FolderVideoAccessError) throw error;
      failures++; status(navigator.onLine ? 'Восстанавливаем соединение…' : 'Ждём подключения к интернету…');
    } finally { clearTimeout(timeout); signal.removeEventListener('abort', abort); }
    await new Promise<void>((resolve, reject) => {
      const abort = () => { clearTimeout(timer); reject(new DOMException('Aborted', 'AbortError')); };
      const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve(); }, folderVideoRetryDelay(failures + 1));
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) abort();
    });
  }
  throw new FolderVideoAccessError('Подготовка ещё не завершилась. Повторите позже.');
}
